import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { finance } from '@/api/client';
import { fmtMoney } from '@/lib/money';
import { fmtDate } from '@/lib/dates';
import { humanStatus } from '@/lib/humanise';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { errorText } from '@/lib/permissionError';

/**
 * DREAM39 — "View Past Invoices" shortcut. Preserves the intent of the
 * originally-specified Invoice Management table (invoice #, study,
 * sponsor, date, amount, status="Generated", no row actions) as a
 * study-scoped modal instead of a page-level table.
 */
export function PastInvoicesModal({
  open,
  onOpenChange,
  studyId,
  studyLabel,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  studyId: string;
  studyLabel: string;
}) {
  const { user } = useAuth();
  const list = useQuery({
    queryKey: ['finance-invoices', user?.activeSiteId, studyId],
    queryFn: () => finance.listInvoices({ study_id: studyId, page_size: 50 }),
    enabled: open && !!user?.activeSiteId,
  });

  async function download(invoiceId: string) {
    try {
      const r = await finance.downloadInvoice(invoiceId);
      window.open(r.download_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(errorText(err, 'Failed to open revenue report'));
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Past revenue reports" description={studyLabel} width={880}>
      {list.isLoading ? (
        <div className="flex items-center justify-center py-10 text-ink-muted">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (list.data?.rows.length ?? 0) === 0 ? (
        <EmptyState title="No revenue reports yet" description="Generated revenue reports for this study will appear here." />
      ) : (
        <table className="data-table w-full text-sm">
          <thead>
            <tr>
              <th>Report #</th>
              <th>Sponsor</th>
              <th>Date</th>
              <th className="text-right">Subject fees</th>
              <th className="text-right">Site fee</th>
              <th className="text-right">Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.data!.rows.map((r) => (
              <tr key={r.invoice_id}>
                <td>
                  <button type="button" className="text-brand-700 hover:underline" onClick={() => download(r.invoice_id)}>
                    {r.invoice_number}
                  </button>
                </td>
                <td>{r.sponsor_name}</td>
                <td>{fmtDate(r.invoice_date)}</td>
                <td className="text-right text-ink-muted">
                  {r.subject_fee_total_cents != null ? fmtMoney(r.subject_fee_total_cents, r.currency_code) : '—'}
                </td>
                <td className="text-right text-ink-muted">
                  {r.fixed_fee_total_cents ? fmtMoney(r.fixed_fee_total_cents, r.currency_code) : '—'}
                </td>
                <td className="text-right font-medium">{fmtMoney(r.total_amount_cents, r.currency_code)}</td>
                <td>{humanStatus(r.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
